<template>
  <v-app id="inspire">
    <v-main>
      <v-container fluid fill-height>
        <v-layout align-center justify-center>
          <v-flex xs12 sm8 md4>
            <v-card class="elevation-12">
              <v-toolbar dark color="primary">
                <v-toolbar-title>{{ $t("users.enter") }}</v-toolbar-title>
              </v-toolbar>
              
              <v-card-text>
                <v-alert v-if="oauthSigninFailed" type="info" outlined dense data-testid="oauth-region-hint">
                  <div>{{ $t('oauth_consent.account_not_found_region') }}</div>
                  <div v-for="connector in oauthAlternativeConnectors" :key="connector.mcp_url || connector.name">
                    {{ $t('oauth_consent.try_connector', { name: connector.name, url: connector.mcp_url }) }}
                  </div>
                </v-alert>
                <!-- OTP Mode (default) -->
                <div v-if="!showPasswordForm">
                  <v-text-field
                    v-model.trim="emailOrPhone"
                    :label="$t('users.email_or_phone')"
                    :hint="$t('users.email_or_phone_hint')"
                    required
                    autocomplete="username webauthn"
                    :loading="isLoading"
                    :disabled="isLoading || otpSent"
                  ></v-text-field>
                  
                  <!-- Дополнительное поле email (показываем если найдено несколько пользователей по телефону) -->
                  <v-expand-transition>
                    <v-text-field 
                      v-if="requiresEmailConfirmation && !otpSent"
                      v-model.trim="emailConfirm"
                      label="Email"
                      :hint="$t('users.multiple_accounts_with_phone')"
                      persistent-hint
                      required
                      autocomplete="email"
                      :loading="isLoading"
                      :disabled="isLoading"
                      class="mt-2"
                    ></v-text-field>
                  </v-expand-transition>
                  
                  <!-- OTP Code Input (shown after code is sent) -->
                  <v-expand-transition>
                    <div v-if="otpSent">
                      <v-alert 
                        color="success" 
                        text 
                        dense 
                        class="mb-4"
                        :icon="false"
                      >
                        <div class="d-flex align-center">
                          <v-icon left color="success">{{ otpMethod === 'telegram' ? 'mdi-send' : 'mdi-email' }}</v-icon>
                          <span>{{ otpMethodMessage }}</span>
                        </div>
                      </v-alert>
                      
                      <!-- OTP Code Input - 6 separate fields -->
                      <div class="otp-input-container">
                        <div class="otp-fields-wrapper">
                          <v-text-field
                            v-for="(digit, index) in otpDigits"
                            :key="index"
                            v-model="otpDigits[index]"
                            :ref="`otpInput${index}`"
                            :name="index === 0 ? 'one-time-code' : `otp-${index}`"
                            :class="['otp-digit-field', { 'otp-verified': otpVerified, 'otp-error': otpError }]"
                            outlined
                            dense
                            :maxlength="index === 0 ? 6 : 1"
                            :disabled="isLoading || otpVerified"
                            @input="handleOtpInput(index)"
                            @keydown="handleOtpKeydown($event, index)"
                            @paste="handleOtpPaste($event, index)"
                            :autocomplete="index === 0 ? 'one-time-code' : 'off'"
                            inputmode="numeric"
                            pattern="[0-9]*"
                          ></v-text-field>
                        </div>
                      </div>
                      
                      <!-- Resend Code Timer -->
                      <div class="text-center mb-3">
                        <v-btn 
                          v-if="canResendOtp"
                          text 
                          small 
                          color="primary"
                          @click="resendOtp()"
                          :disabled="isLoading"
                        >
                          <v-icon left small>mdi-refresh</v-icon>
                          {{ $t('users.resend_code') }}
                        </v-btn>
                        <span v-else class="text--secondary caption">
                          {{ $t('users.resend_in') }} {{ resendTimer }}с
                        </span>
                      </div>
                    </div>
                  </v-expand-transition>
                  
                  <!-- Passkey как основной способ входа (если есть) -->
                  <v-expand-transition>
                    <div v-if="passkeySupported && userHasPasskeys && !otpSent" class="text-center mt-4">
                      <v-btn
                        block
                        :loading="passkeyLoading"
                        :disabled="isLoading || passkeyLoading"
                        @click="loginWithPasskey()"
                        class="passkey-btn"
                      >
                        <v-icon left>mdi-key-variant</v-icon>
                        {{ $t("passkeys.login_with_passkey") }}
                      </v-btn>
                      
                      <v-divider class="my-4"></v-divider>
                      <span class="caption text--secondary">{{ $t("custom.or") }}</span>
                    </div>
                  </v-expand-transition>
                  
                  <v-flex class="text-center mt-4">
                    <v-btn 
                      v-if="!otpSent"
                      block 
                      :color="userHasPasskeys ? '' : 'primary'"
                      :outlined="userHasPasskeys"
                      :loading="isLoading"
                      :disabled="isLoading || (requiresEmailConfirmation && !emailConfirm)"
                      @click="requestOtp()"
                    >
                      <v-icon left>mdi-send</v-icon>
                      {{ $t("users.get_code") }}
                    </v-btn>
                    
                    <v-btn 
                      v-if="otpSent"
                      text 
                      small
                      class="mt-2"
                      @click="resetOtpForm()"
                      :disabled="isLoading"
                    >
                      {{ $t('users.back') }}
                    </v-btn>
                  </v-flex>
                </div>
                
                <!-- Password Mode (hidden by default) -->
                <v-expand-transition>
                  <div v-if="showPasswordForm">
                    <v-text-field 
                      v-model.trim="email" 
                      data-cy="email" 
                      label="Email" 
                      required
                      autocomplete="email" 
                      :loading="isLoading"
                      :disabled="isLoading"
                    ></v-text-field>
                    
                    <v-text-field 
                      v-model.trim="password" 
                      :append-icon="show1 ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                      :type="show1 ? 'text' : 'password'" 
                      name="input-10-1" 
                      :hint="$t('users.password_length')" 
                      counter
                      @click:append="show1 = !show1" 
                      data-cy="password" 
                      :label="$t('users.password')" 
                      required
                      autocomplete="current-password" 
                      :loading="isLoading"
                      :disabled="isLoading"
                      @keyup.enter="signin()"
                    ></v-text-field>
                    
                    <v-flex class="text-center mt-4">
                      <v-btn 
                        type="submit" 
                        data-cy="submit" 
                        block 
                        color="primary" 
                        :loading="isLoading"
                        :disabled="isLoading"
                        @click="signin()"
                      >
                        {{ $t("users.enter") }}
                      </v-btn>
                      
                      <v-btn 
                        text 
                        small
                        class="mt-2"
                        @click="switchToOtpForm()"
                        :disabled="isLoading"
                      >
                        <v-icon left small>mdi-arrow-left</v-icon>
                        {{ $t('users.back_to_code') }}
                      </v-btn>
                    </v-flex>
                  </div>
                </v-expand-transition>
                
                <!-- Links -->
                <div class="text-center mt-4 links-section">
                  <div>
                    <router-link to="/signup">
                      {{ $t("users.registration") }}
                    </router-link>
                    <span class="mx-2">•</span>
                    <router-link to="/forgot_password">
                      {{ $t("users.password_lost") }}
                    </router-link>
                  </div>
                  
                  <!-- FT-191: на общем компьютере — вход по PIN с экрана блокировки -->
                  <div class="mt-2" v-if="pinSignInAvailable">
                    <v-btn text small color="primary" data-test="signin-with-pin" @click="signInWithPin">
                      <v-icon small left>mdi-dialpad</v-icon>{{ $t("device_lock.sign_in_with_pin") }}
                    </v-btn>
                  </div>
                  <div class="mt-2" v-if="passwordLinkVisible">
                    <a 
                      href="#" 
                      @click.prevent="switchToPasswordForm()"
                      class="password-link text--secondary"
                    >
                      <v-icon small class="mr-1">mdi-lock-outline</v-icon>
                      {{ $t("users.login_with_password") }}
                    </a>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { Capacitor } from "@capacitor/core";
import Cookies from "js-cookie";
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  isNativeApp,
  isConditionalMediationAvailable,
  getCredential
} from '@/utils/webauthn';
import { markEnrolledHere } from "@/utils/passkeyNudge"; // FT-079
import { takeHandoffToken } from "@/utils/handoffFragment";
import { supportedLocale } from "@/utils/localeInfra";
import { takePendingLogin } from "@/utils/deviceLock"; // FT-191
import { emitDeviceEvent } from "@/utils/deviceTabs"; // FT-191
import { DEFAULT_API_BASE } from "@/utils/apiBase";
import { oauthApiBaseForRoute } from "@/utils/oauthFlow";
import { postSigninPath } from "@/router/authRedirect";
// CryptoJS removed - no longer encrypting credentials
// Biometric auth removed - using simple auto-fill instead
export default {
  name: "Signin",
  data() {
    return {
      emailOrPhone: null, // Изменили с email на emailOrPhone
      email: null, // Сохраняем для password mode
      emailConfirm: null, // Дополнительный email для уточнения
      requiresEmailConfirmation: false, // Флаг необходимости указать email
      otpUserEmail: null, // Email пользователя, полученный от бэкенда при fallback
      password: null,
      error: "",
      show1: false,
      push_granted: false,
      fcm_token: null,
      isLoading: false,
      // OTP related
      showPasswordForm: false, // false = OTP form (default), true = password form
      otpSent: false,
      otpCode: '',
      otpDigits: ["", "", "", "", "", ""], // Массив для отдельных цифр
      otpMethod: null, // 'telegram' or 'email'
      otpRequestId: null,
      otpVerified: false, // Флаг успешной верификации
      otpError: false, // Флаг ошибки верификации
      resendTimer: 60,
      resendTimerInterval: null,
      otpSyncInterval: null,
      canResendOtp: false,
      // Passkeys
      passkeySupported: false,
      passkeyPlatformAvailable: false,
      passkeyLoading: false,
      userHasPasskeys: false,
      passkeyCheckTimeout: null,
      oauthSigninFailed: false,
      oauthAlternativeConnectors: [],
    };
  },
  async created() {
    // Проверяем поддержку Passkeys
    this.checkPasskeySupport();
    
    // Автоматически подставляем сохраненный логин (если включена опция "Запомнить логин")
    const rememberedLogin = localStorage.getItem("rememberedLogin");
    if (rememberedLogin) {
      this.emailOrPhone = rememberedLogin;
      this.email = rememberedLogin; // Для password mode
    } else if (this.isNative) {
      // Для мобильных приложений - fallback на последний использованный email
      const lastUsedEmail = localStorage.getItem("lastUsedEmail");
      if (lastUsedEmail) {
        this.emailOrPhone = lastUsedEmail;
        this.email = lastUsedEmail; // Для password mode
      }
    }
    
    // FT-098: тикет берём из модуля-санитайзера, а не из $route.hash — фрагмент
    // снят ещё в main.js, до конструирования Rollbar и Crisp.
    const handoffToken = takeHandoffToken();
    if (handoffToken) {
      this.exchangeHandoff(handoffToken);
    }

    // FT-191 REQ-09: смена сотрудника на общем компьютере — ответ разблокировки оставлен в
    // sessionStorage перед перезагрузкой; применяем его штатным путём входа.
    const pendingLogin = takePendingLogin();
    if (pendingLogin) {
      this.completePendingDeviceLogin(pendingLogin);
    }

    if (this.$route.query && this.$route.query.locale) {
      this.applyLocale(this.$route.query.locale);
    }
    if (this.$route.query && this.$route.query.email) {
      // Default login mode uses the combined "email or phone" field; the
      // password-mode field is a separate one. Prefill both.
      this.email = this.$route.query.email;
      this.emailOrPhone = this.$route.query.email;
    }
    // Некоторые старые входные сценарии вызывают hook напрямую без экземпляра
    // Vue; в настоящем компоненте метод всегда установлен Options API.
    if (this.loadOAuthConnectors) this.loadOAuthConnectors();
    // FT-098 (Задача 11, NS-05): пароль из query-строки больше НЕ читаем.
    // Лендинг передаёт одноразовый handoff-тикет во фрагменте (см.
    // exchangeHandoff выше) — пароль в URL/логи/Referer не попадает.
    // Удалено ПОСЛЕ выката лендинга с фрагментом (CON-03).
  },
  mounted() {
    // FT-079 conditional UI (autofill passkey) ВРЕМЕННО ОТКЛЮЧЕНО (прод-инцидент 2026-07-03):
    // параллельный conditional get() держал аутентификатор занятым → модальный вход по кнопке
    // «Войти по passkey» падал (OperationError «passkey not found») на вебе, хотя ключ на устройстве есть.
    // Кнопочный вход восстановлен. Вернуть autofill после проверки на реальных браузерах
    // (abort-guard в loginWithPasskey/startConditionalUI уже на месте).
    // this.startConditionalUI();
  },
  beforeDestroy() {
    this.stopResendTimer();
    this.stopOtpNativeSync();
    if (this.passkeyCheckTimeout) {
      clearTimeout(this.passkeyCheckTimeout);
    }
    if (this._passkeyCUIAbort) this._passkeyCUIAbort.abort(); // FT-079: остановить conditional UI
  },
  watch: {
    otpSent: {
      handler(sent) {
        if (sent) this.startOtpNativeSync();
        else this.stopOtpNativeSync();
      },
      immediate: true,
    },
    // Проверяем наличие Passkeys и регион при вводе email/телефона
    emailOrPhone(newValue) {
      this.userHasPasskeys = false;

      if (this.passkeyCheckTimeout) {
        clearTimeout(this.passkeyCheckTimeout);
      }

      // Debounce - проверяем через 500ms после окончания ввода
      // Вызываем всегда (не только при passkeySupported) для определения региона
      if (newValue && newValue.length >= 5) {
        this.passkeyCheckTimeout = setTimeout(() => {
          this.checkUserPasskeys(newValue);
        }, 500);
      }
    },
  },
  computed: {
    // Пароль нужен агентам и партнёрам и на общем компьютере (NS-04); сотрудникам компании с
    // блокировкой его закрывает сервер (403 password_login_disabled).
    passwordLinkVisible() {
      return !this.showPasswordForm;
    },
    // FT-191: общий компьютер с сотрудниками — можно войти по PIN, не заходя по коду.
    pinSignInAvailable() {
      const device = this.$store.getters["deviceLock/device"];
      return !this.isNative && !!device && Array.isArray(device.users) && device.users.length > 0;
    },
    isNative() {
      return Capacitor.getPlatform().toLowerCase() == "ios" ||
        Capacitor.getPlatform().toLowerCase() == "android"
        ? true
        : false;
    },
    isPushNotificationsAvailable() {
      return Capacitor.isPluginAvailable("PushNotifications");
    },
    otpMethodMessage() {
      if (this.otpMethod === 'telegram') {
        return this.$t('users.code_sent_telegram') || 'Код отправлен в Telegram. Если его там нет, проверьте ваш email';
      } else if (this.otpMethod === 'email') {
        return this.$t('users.code_sent_email') || 'Код отправлен на ваш email';
      }
      return '';
    },
    baseURL() {
      // FT-018: getter всегда non-null (state.api_url → VUE_APP_API_URL → fallback chain).
      return oauthApiBaseForRoute(this.$route, DEFAULT_API_BASE) || this.$store.getters.getApiUrl;
    },
    oauthApiBase() {
      return oauthApiBaseForRoute(this.$route, DEFAULT_API_BASE);
    },
  },
  methods: {
    async loadOAuthConnectors() {
      if (!this.oauthApiBase) return;
      try {
        const response = await this.axios({
          url: "/oauth/connectors",
          baseURL: this.oauthApiBase,
          method: "get",
          skipStoredAuth: true,
        });
        const connectors = Array.isArray(response.data && response.data.connectors)
          ? response.data.connectors
          : [];
        this.oauthAlternativeConnectors = connectors
          .filter((connector) => connector && connector.current !== true);
      } catch {
        this.oauthAlternativeConnectors = [];
      }
    },
    markOAuthSigninFailure() {
      if (this.oauthApiBase) this.oauthSigninFailed = true;
    },
    finishSigninNavigation() {
      const path = postSigninPath({
        from: this.$route.query && this.$route.query.from,
        isAgent: this.isAgent,
      });
      this.$router.push({ path }).catch(() => {});
    },
    // FT-191: после любого успешного входа — режим общего компьютера по ответу сервера, снятая
    // блокировка и свежая метка активности (иначе просроченная метка тут же заблокирует снова).
    signInWithPin() {
      this.$store.commit("deviceLock/LOCK", { userId: null });
    },
    // FT-191 DEC-02: компания запретила вход по паролю — объясняем и возвращаем к входу по коду.
    handlePasswordLoginDisabled(error) {
      if (!(error?.response?.status === 403 && error.response.data?.error?.code === "password_login_disabled")) {
        return false;
      }
      this.showPasswordForm = false;
      this.$notify(this.$t("errors.enter_title"), this.$t("device_lock.password_login_disabled"), "warning", 8000);
      return true;
    },
    completePendingDeviceLogin(response) {
      this.$store.commit("setUserInfo", response);
      this.$store.commit("setUserSettings", response.data.user_settings);
      Cookies.set("auth_token", response.headers.authorization, { expires: 365 });
      // Режим устройства, снятую блокировку и метку активности выставляет deviceLockPlugin на setUserInfo.
      this.$vuetify.theme.dark = !!(response.data.user_settings && response.data.user_settings.dark_theme);
      // Новый сотрудник записан в хранилище — закрытые вкладки могут перезагружаться.
      emitDeviceEvent("switch_ready");
      this.getUserInfo();
    },
    // ===== УТИЛИТЫ =====
    // Локаль из профиля на сервере или из ссылки — только если такая локаль в
    // проекте есть. Иначе $i18n.locale встал бы на мёртвый код (так было бы у
    // каждого, у кого в профиле остался удалённый хинди): тексты ушли бы на
    // fallback, а селектор языка показывал бы пустоту, и само не починилось бы.
    applyLocale(candidate) {
      const locale = supportedLocale(candidate, Object.keys(this.$i18n.messages));
      if (locale) this.$i18n.locale = locale;
    },
    updateRememberedLogin(userSettings, login) {
      // Обновляем rememberedLogin в localStorage в зависимости от настройки пользователя
      if (userSettings && userSettings.remember_login && login) {
        localStorage.setItem("rememberedLogin", login);
      } else {
        // Если опция выключена, удаляем сохраненный логин
        localStorage.removeItem("rememberedLogin");
      }
    },
    
    signin() {
      if ((this.email && this.password && this.email.trim() && this.password.trim())) {
        let self = this;
        this.isLoading = true;
        let data = {
          user: {
            email: this.email,
            password: this.password,
          },
        };
        this.axios({
          url: "users/sign_in",
          baseURL: this.baseURL, // FT-018: computed property выше
          method: "post",
          data: data,
          skipStoredAuth: !!this.oauthApiBase,
        })
          .then((response) => {
            this.$store.commit("setUserInfo", response);
            this.$store.commit("setUserSettings", response.data.user_settings);
            Cookies.set("auth_token", response.headers.authorization, {
              expires: 365,
            });
            this.markLoginMethod("password"); // FT-079
            // Обновляем rememberedLogin если включена опция
            this.updateRememberedLogin(response.data.user_settings, this.email);
            if (
              response.data.user_settings &&
              response.data.user_settings.dark_theme
            ) {
              this.$vuetify.theme.dark = true;
            } else {
              this.$vuetify.theme.dark = false;
            }
          })
          .then(() => {
            this.getUserInfo();
          })
          .catch((error) => {
            this.isLoading = false;
            this.markOAuthSigninFailure();
            if (this.oauthApiBase) {
              this.$notify(this.$t("errors.enter_title"), this.$t("errors.enter_text"), "error", 5000);
            } else if (this.handlePasswordLoginDisabled(error)) {
              // FT-191: сообщение показано
            } else if (
              error.response &&
              error.response?.status &&
              error.response?.status == 401
            ) {
              this.$notify(this.$t("errors.enter_title"), this.$t("errors.enter_text"), "error", 5000);
            } else if (
              error.response &&
              error.response?.status &&
              error.response?.status == 404
            ) {
              this.$notify(this.$t("errors.enter_title"), this.$t("errors.lost_email"), "error", 5000);
            } else {
              this.$notify(this.$t("errors.enter_title"), error, "error", 5000);
            }
            console.log(error);
          }).finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$notify(this.$t("errors.all_fields_title"), this.$t("errors.all_fields_text"), "error", 5000);
      }
    },
    // FT-098 [REQ-06]: обмен одноразового тикета на JWT. Ветка успеха
    // намеренно совпадает с signin() — ответ обмена по форме равен обычному входу.
    exchangeHandoff(token) {
      this.isLoading = true;
      this.axios({
        url: "api/v1/auth/handoff",
        baseURL: this.baseURL,
        method: "post",
        data: { handoff_token: token },
        skipStoredAuth: !!this.oauthApiBase,
      })
        .then((response) => {
          this.$store.commit("setUserInfo", response);
          this.$store.commit("setUserSettings", response.data.user_settings);
          Cookies.set("auth_token", response.headers.authorization, { expires: 365 });
          this.markLoginMethod("handoff");
          if (response.data.user_settings && response.data.user_settings.dark_theme) {
            this.$vuetify.theme.dark = true;
          }
          // ВАЖНО: не звать signinSuccessful() напрямую. Обе существующие ветки
          // входа идут через getUserInfo() — там /me.json, локаль пользователя и
          // гейты «аккаунт активен» и «агенту закрыт доступ». Прямой вызов их
          // обошёл бы: сессия была бы живой, но без проверок и без локали.
          this.getUserInfo();
        })
        .catch(() => {
          // Тикет протух, погашен или битый — все случаи дают 401 и одинаковую
          // реакцию: обычная форма входа с подставленным email. Повторять обмен
          // бессмысленно, тикет одноразовый.
          this.isLoading = false;
          this.markOAuthSigninFailure();
        });
    },
    getUserInfo() {
      let self = this;
      this.axios
        .get("/me.json", {
          baseURL: this.baseURL,
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          if (response.data.active && response.data.role != "agent") {
            this.$store.commit("setCurrentUser", response.data);
            // Set locale
            this.applyLocale(response.data.locale);
            this.signinSuccessful();
          } else if (response.data.active && response.data.role == "agent" && response.data.agent_setting) {
            if (response.data.agent_setting.access_to_account == false) {
              this.$store.commit("unsetCurrentUser");
            } else {
              this.$store.commit("setCurrentUser", response.data);
              // Set locale
              this.applyLocale(response.data.locale);
              this.signinSuccessful();
            }
          } else {
            this.isLoading = false;
            this.$notify(this.$t("errors.deactivation_title"), this.$t("errors.deactivation_text"), "error", 5000);
          }
        })
        .catch(function (error) {
          console.log("getUserInfo", error);
          self.$store.commit("unsetCurrentUser");
          self.isLoading = false;
          self.markOAuthSigninFailure();
          // self.$router.push({ path: `/signin` }).catch(err => {});
          console.log(error);
        });
    },
    // Проверяем доступность биометрии и наличие сохраненных credentials
    signinSuccessful() {
      let self = this;
      this.isLoading = true;
      
      const token = self.$store.getters.getAuthToken;
      
      this.axios
        .get(`/api/v1/company`, {
          baseURL: this.baseURL,
          headers: {
            Authorization: token,
          },
        })
        .then((response) => {
          this.$store.commit("setCompany", { company: response.data });
          self.axios
            .get(`api/v1/main_companies/${response.data.main_company_id}`, {
              baseURL: self.baseURL,
              headers: {
                Authorization: self.$store.getters.getAuthToken,
              },
            })
            .then((response_main_company) => {
              self.$store.commit("setMainCompany", response_main_company.data);
            })
            .catch((error) => {
              self.isLoading = false;
              self.setError(error, self.$t("errors.main_company_data"));
              console.log(error);
            });
        })
        .then(() => {
          self.axios
            .get(`api/v1/doc_templates/${null}`, {
              baseURL: self.baseURL,
              headers: {
                Authorization: self.$store.getters.getAuthToken,
              },
            })
            .then((response_data) => {
              if (response_data.data) {
                self.$store.commit("setDocUrl", response_data.data.url);
              } else {
                let doc_link =
                  self.$i18n.locale == "ru"
                    ? "https://web.rentprog.ru/doc_example.docx"
                    : "https://web.rentprog.com/doc_example_en.docx";
                self.$store.commit("setDocUrl", doc_link);
              }
            })
            .catch((error) => {
              self.isLoading = false;
              self.setError(error, self.$t("errors.docs_data"));
              console.log(error);
            });
          self.axios
            .get(`api/v1/doc_entity_templates/${null}`, {
              baseURL: self.baseURL,
              headers: {
                Authorization: self.$store.getters.getAuthToken,
              },
            })
            .then((doc_entity_response) => {
              // Если есть договор, то он, если нет, то по умолчанию шаблон
              if (doc_entity_response.data) {
                self.$store.commit(
                  "setEntityDocUrl",
                  doc_entity_response.data.url
                );
              } else {
                let doc_link =
                  self.$i18n.locale == "ru"
                    ? "https://web.rentprog.ru/entity_doc_example.docx"
                    : "https://web.rentprog.com/entity_doc_example_en.docx";
                self.$store.commit("setEntityDocUrl", doc_link);
              }
            })
            .catch((error) => {
              self.isLoading = false;
              self.setError(error, self.$t("errors.docs_data"));
              console.log(error);
            });
          self.axios
            .get(`api/v1/invoice_templates`, {
              baseURL: self.baseURL,
              headers: {
                Authorization: self.$store.getters.getAuthToken,
              },
            })
            .then((invoice_response) => {
              // Если есть договор, то он, если нет, то по умолчанию шаблон
              if (invoice_response.data) {
                self.$store.commit("setInvoiceUrl", invoice_response.data.url);
              } else {
                self.$store.commit(
                  "setInvoiceUrl",
                  "https://web.rentprog.ru/invoice_example.docx"
                );
              }
            })
            .catch((error) => {
              self.isLoading = false;
              self.setError(error, self.$t("errors.docs_data"));
              console.log(error);
            });
        })
        .catch((error) => {
          self.isLoading = false;
          self.setError(error, self.$t("errors.company_data"));
          console.log(error);
        })
        .finally(() => {
          this.isLoading = false;
          // Push notifications registration moved to AppHeader.vue
          this.finishSigninNavigation();
        });
    },
    signinFailed(error) {
      this.markOAuthSigninFailure();
      this.setError(error, this.$t("errors.auth"));
      this.$store.commit("unsetCurrentUser");
      console.log(this.error);
    },
    
    async requestOtp() {
      if (!this.emailOrPhone || !this.emailOrPhone.trim()) {
        this.$notify(this.$t("errors.error"), this.$t("errors.email_or_phone_required"), "error", 5000);
        return;
      }

      this.isLoading = true;
      try {
        const requestData = {
          email: this.emailOrPhone, // API endpoint принимает "email", но мы посылаем телефон или email
          locale: this.$i18n.locale,
        };
        
        // Если требуется подтверждение email, добавляем его
        if (this.requiresEmailConfirmation && this.emailConfirm) {
          requestData.email_confirm = this.emailConfirm;
        }
        
        const response = await this.axios({
          url: "/api/v1/auth/request_otp",
          baseURL: this.baseURL,
          method: "post",
          data: requestData,
          skipStoredAuth: !!this.oauthApiBase,
        });

        this.otpSent = true;
        this.otpMethod = response.data.method; // 'telegram' or 'email'
        this.otpRequestId = response.data.request_id || null;
        this.otpUserEmail = response.data.email || null; // Сохраняем email от бэкенда
        this.startResendTimer();
        this.requiresEmailConfirmation = false; // Сбрасываем флаг после успешной отправки

        // Фокусируемся на первом поле ввода кода
        this.focusFirstOtpInput();
      } catch (error) {
        console.error("OTP Request Error:", error);
        this.markOAuthSigninFailure();
        
        // Проверяем, требуется ли указать email
        if (this.oauthApiBase) {
          this.$notify(this.$t("errors.error"), this.$t("errors.otp_request_failed"), "error", 5000);
        } else if (error.response?.data?.requires_email) {
          this.requiresEmailConfirmation = true;
          this.$notify(
            this.$t("errors.attention"), 
            error.response?.data.error || this.$t("errors.multiple_users_specify_email"), 
            "warning", 
            7000
          );
        } else {
          const errorMessage = error.response?.data?.error || this.$t("errors.otp_request_failed");
          this.$notify(this.$t("errors.error"), errorMessage, "error", 5000);
        }
      } finally {
        this.isLoading = false;
      }
    },

    async verifyOtp() {
      if (!this.otpCode || this.otpCode.length !== 6) {
        this.$notify(this.$t("errors.error"), this.$t("errors.invalid_code_length"), "error", 5000);
        return;
      }

      this.isLoading = true;
      try {
        const verifyData = {
          code: this.otpCode,
          method: this.otpMethod,
          request_id: this.otpRequestId,
        };
        
        // Отправляем email для верификации
        // Приоритет:
        // 1. Email от бэкенда (при fallback с телефона на email)
        // 2. Email если напрямую ввели email
        // 3. Email подтверждения (при множественных телефонах)
        // 4. null (для уникального телефона с Telegram)
        if (this.otpUserEmail) {
          verifyData.email = this.otpUserEmail;
        } else if (this.isEmail(this.emailOrPhone)) {
          verifyData.email = this.emailOrPhone;
        } else if (this.emailConfirm) {
          verifyData.email = this.emailConfirm;
        } else {
          // Если входили по телефону и телефон был уникальным,
          // бэкенд найдет пользователя по request_id
          verifyData.email = null;
        }
        
        const response = await this.axios({
          url: "/api/v1/auth/verify_otp",
          baseURL: this.baseURL,
          method: "post",
          data: verifyData,
          skipStoredAuth: !!this.oauthApiBase,
        });

        // Здесь стоял блок «нормализации» заголовка authorization:
        //   if (!response.headers.authorization && response.headers['authorization'])
        // Обе части — ОДНО И ТО ЖЕ свойство (точечная и скобочная запись одного
        // ключа), поэтому условие имело вид `!x && x`, было тождественно ложно и
        // тело не выполнялось ни разу; внутри к тому же было самоприсваивание.
        // Той же природы было и `headers.authorization || headers['authorization']`
        // — `x || x`; такая запись стояла на всех трёх участках входа и сведена
        // к одному чтению везде.
        // Если заголовок реально приходит в другом регистре, нормализацию надо
        // писать заново и от источника, а не от самого себя.

        // Store JWT token
        this.$store.commit("setUserInfo", response);
        this.$store.commit("setUserSettings", response.data.user_settings);

        const token = response.headers.authorization;
        
        console.log('verifyOtp: JWT token received from server, length:', token?.length);
        
        // Токен должен храниться С префиксом "Bearer " для корректной работы JWT
        Cookies.set("auth_token", token, {
          expires: 365,
        });
        this.markLoginMethod("otp"); // FT-079

        // Wait for next tick to ensure store is updated
        await this.$nextTick();

        // Обновляем rememberedLogin если включена опция
        const loginToSave = this.otpUserEmail || (this.isEmail(this.emailOrPhone) ? this.emailOrPhone : (this.emailConfirm || this.emailOrPhone));
        this.updateRememberedLogin(response.data.user_settings, loginToSave);

        // Set theme
        if (response.data.user_settings && response.data.user_settings.dark_theme) {
          this.$vuetify.theme.dark = true;
        } else {
          this.$vuetify.theme.dark = false;
        }

        // Показываем зеленое выделение полей успеха
        this.otpVerified = true;
        
        // Небольшая задержка для показа анимации успеха
        await new Promise(resolve => setTimeout(resolve, 600));

        // Continue with normal sign in flow - await before finally
        // Передаем токен напрямую, чтобы избежать проблем с асинхронностью store
        await this.getUserInfoAsync(token);
      } catch (error) {
        console.error("OTP Verification Error:", error);
        this.markOAuthSigninFailure();
        const errorMessage = this.oauthApiBase
          ? this.$t("errors.otp_verification_failed")
          : (error.response?.data?.error || this.$t("errors.otp_verification_failed"));
        
        // Показываем красное выделение полей ошибки
        this.otpError = true;
        this.otpVerified = false;
        
        // Ждем завершения анимации ошибки
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Очищаем поля после анимации
        this.otpDigits = ["", "", "", "", "", ""];
        this.otpCode = "";
        this.otpError = false;
        
        // Фокусируемся на первом поле
        this.$nextTick(() => {
          const firstInput = this.$refs['otpInput0'];
          if (firstInput && firstInput[0]) {
            firstInput[0].focus();
          }
        });
        
        this.$notify(this.$t("errors.error"), errorMessage, "error", 5000);
        this.isLoading = false;
      }
    },

    async resendOtp() {
      this.otpCode = '';
      this.otpDigits = ["", "", "", "", "", ""];
      this.otpVerified = false;
      this.otpError = false;
      await this.requestOtp();
    },

    resetOtpForm() {
      this.otpSent = false;
      this.otpCode = '';
      this.otpDigits = ["", "", "", "", "", ""];
      this.otpMethod = null;
      this.otpRequestId = null;
      this.otpUserEmail = null;
      this.otpVerified = false;
      this.otpError = false;
      this.requiresEmailConfirmation = false;
      this.emailConfirm = null;
      this.stopResendTimer();
      
      // Если есть сохраненный email, восстанавливаем его в поле
      if (this.savedEmail) {
        this.emailOrPhone = this.savedEmail;
      }
    },
    
    switchToPasswordForm() {
      this.showPasswordForm = true;
      // Если есть сохраненный email, заполняем поле email в форме пароля
      if (this.savedEmail) {
        this.email = this.savedEmail;
      } else if (this.emailOrPhone) {
        // Или используем то, что пользователь уже ввел
        this.email = this.emailOrPhone;
      }
    },
    
    switchToOtpForm() {
      this.showPasswordForm = false;
      // Если есть сохраненный email, заполняем поле emailOrPhone в форме OTP
      if (this.savedEmail) {
        this.emailOrPhone = this.savedEmail;
      } else if (this.email) {
        // Или используем то, что пользователь уже ввел в форме пароля
        this.emailOrPhone = this.email;
      }
    },

    startResendTimer() {
      this.canResendOtp = false;
      this.resendTimer = 60;
      
      if (this.resendTimerInterval) {
        clearInterval(this.resendTimerInterval);
      }
      
      this.resendTimerInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) {
          this.canResendOtp = true;
          this.stopResendTimer();
        }
      }, 1000);
    },

    stopResendTimer() {
      if (this.resendTimerInterval) {
        clearInterval(this.resendTimerInterval);
        this.resendTimerInterval = null;
      }
    },

    // Автоподстановка кода из письма в приложении (WKWebView на iOS) вписывает код в
    // нативный <input> без события `input`: v-text-field этого не видит, и раскладка по
    // ячейкам с отправкой не запускаются — весь код висит в первой ячейке. Пока открыт
    // ввод кода, сверяем текст полей с тем, что знает Vuetify, и при расхождении
    // повторяем пропущенное событие — дальше работает обычный handleOtpInput.
    startOtpNativeSync() {
      this.stopOtpNativeSync();
      this.otpSyncInterval = setInterval(this.syncOtpFromNative, 300);
    },

    stopOtpNativeSync() {
      if (this.otpSyncInterval) {
        clearInterval(this.otpSyncInterval);
        this.otpSyncInterval = null;
      }
    },

    syncOtpFromNative() {
      if (this.isLoading || this.otpVerified) return;
      for (let i = 0; i < 6; i++) {
        const field = this.$refs[`otpInput${i}`] && this.$refs[`otpInput${i}`][0];
        const native = field && field.$refs.input;
        if (native && native.value !== (field.lazyValue || '')) {
          native.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
      }
    },

    // OTP Digit Input Handlers
    handleOtpInput(index) {
      const value = this.otpDigits[index];
      
      // Сбрасываем ошибку при новом вводе
      if (this.otpError) {
        this.otpError = false;
      }
      
      // Если вставлено больше одного символа (автозаполнение на мобильных)
      if (value && value.length > 1) {
        const digits = value.replace(/\D/g, ''); // Убираем нецифровые символы
        
        if (digits.length === 6) {
          // Распределяем цифры по всем полям
          for (let i = 0; i < 6; i++) {
            this.$set(this.otpDigits, i, digits[i] || '');
          }
          
          // Фокусируемся на последнем поле
          this.$nextTick(() => {
            const lastInput = this.$refs['otpInput5'];
            if (lastInput && lastInput[0]) {
              lastInput[0].focus();
            }
          });
          
          // Проверяем код
          this.checkOtpComplete();
          return;
        } else {
          // Если не 6 цифр, берем только первую
          this.$set(this.otpDigits, index, digits[0] || '');
        }
      }
      
      // Разрешаем только цифры
      if (value && !/^\d$/.test(value)) {
        this.$set(this.otpDigits, index, '');
        return;
      }
      
      // Если введена цифра, переходим к следующему полю
      if (value && index < 5) {
        const nextInput = this.$refs[`otpInput${index + 1}`];
        if (nextInput && nextInput[0]) {
          nextInput[0].focus();
        }
      }
      
      // Проверяем, заполнены ли все поля
      this.checkOtpComplete();
    },

    handleOtpKeydown(event, index) {
      // Backspace - переход к предыдущему полю
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        const prevInput = this.$refs[`otpInput${index - 1}`];
        if (prevInput && prevInput[0]) {
          prevInput[0].focus();
        }
      }
      
      // ArrowLeft - переход к предыдущему полю
      if (event.key === 'ArrowLeft' && index > 0) {
        const prevInput = this.$refs[`otpInput${index - 1}`];
        if (prevInput && prevInput[0]) {
          prevInput[0].focus();
        }
      }
      
      // ArrowRight - переход к следующему полю
      if (event.key === 'ArrowRight' && index < 5) {
        const nextInput = this.$refs[`otpInput${index + 1}`];
        if (nextInput && nextInput[0]) {
          nextInput[0].focus();
        }
      }
    },

    handleOtpPaste(event, index) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text').trim();
      
      // Проверяем, что вставлен код из 6 цифр
      if (/^\d{6}$/.test(pastedData)) {
        const digits = pastedData.split('');
        
        // Заполняем все поля
        digits.forEach((digit, i) => {
          if (i < 6) {
            this.$set(this.otpDigits, i, digit);
          }
        });
        
        // Фокусируемся на последнем поле
        const lastInput = this.$refs['otpInput5'];
        if (lastInput && lastInput[0]) {
          lastInput[0].focus();
        }
        
        // Проверяем код
        this.checkOtpComplete();
      }
    },

    checkOtpComplete() {
      // Собираем код из отдельных полей
      this.otpCode = this.otpDigits.join('');
      
      // Если все 6 цифр введены, автоматически проверяем код
      if (this.otpCode.length === 6 && !this.isLoading && !this.otpVerified) {
        this.verifyOtp();
      }
    },
    
    // Вспомогательный метод для установки фокуса на первое поле OTP
    focusFirstOtpInput() {
      this.$nextTick(() => {
        const firstInput = this.$refs['otpInput0'];
        if (firstInput && firstInput[0]) {
          // Vuetify v-text-field компонент
          const vTextField = firstInput[0];
          
          // Пытаемся получить нативный input элемент
          if (vTextField.$el && vTextField.$el.querySelector) {
            const inputElement = vTextField.$el.querySelector('input');
            if (inputElement) {
              inputElement.focus();
              
              // Для iOS - дополнительно кликаем для активации автозаполнения
              if (this.isNative) {
                inputElement.click();
              }
              
              console.log('OTP input focused for autofill');
            }
          } else if (vTextField.focus) {
            // Fallback - вызываем метод focus компонента
            vTextField.focus();
          }
        }
      });
    },
    
    // Вспомогательный метод для проверки, является ли строка email
    isEmail(str) {
      if (!str) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(str);
    },

    // FT-079: conditional UI (autofill passkey) — web-only, тихо, discoverable (без email).
    async startConditionalUI() {
      if (this.isNative || !isWebAuthnSupported()) return;
      try {
        if (!(await isConditionalMediationAvailable())) return;
      } catch {
        return;
      }
      try {
        const optionsResponse = await this.axios({
          url: "/api/v1/passkeys/authentication_options",
          baseURL: this.baseURL,
          method: "post",
          data: {},
          skipStoredAuth: !!this.oauthApiBase,
        });
        const authSessionId = optionsResponse.data.auth_session_id;
        // FT-079 fix: если уже идёт модальный вход по passkey — не стартуем
        // conditional get() (иначе два параллельных запроса конфликтуют).
        if (this._passkeyModalActive) return;
        this._passkeyCUIAbort = new AbortController();
        let credentialData;
        try {
          credentialData = await getCredential(optionsResponse.data, {
            mediation: "conditional",
            signal: this._passkeyCUIAbort.signal,
          });
        } catch {
          return; // отмена/abort/нет выбора — тихо
        }
        if (!credentialData) return;
        const response = await this.axios({
          url: "/api/v1/passkeys/authenticate",
          baseURL: this.baseURL,
          method: "post",
          data: { credential: credentialData, auth_session_id: authSessionId },
          skipStoredAuth: !!this.oauthApiBase,
        });
        this.$store.commit("setUserInfo", response);
        this.$store.commit("setUserSettings", response.data.user_settings);
        const token = response.headers.authorization;
        Cookies.set("auth_token", token, { expires: 365 });
        this.markLoginMethod("passkey");
        markEnrolledHere(response.data.user && response.data.user.id); // FT-079: этот аккаунт использовал passkey на устройстве → nudge не нужен
        this.updateRememberedLogin(
          response.data.user_settings,
          this.emailOrPhone || (response.data.user && response.data.user.email)
        );
        this.$vuetify.theme.dark = !!(response.data.user_settings && response.data.user_settings.dark_theme);
        await this.getUserInfoAsync(token);
      } catch {
        // conditional UI не должен ломать логин — тихо
        this.markOAuthSigninFailure();
      }
    },
    // FT-079: запомнить метод входа для post-login passkey-nudge (только 'passkey' подавляет подсказку).
    markLoginMethod(method) {
      try { sessionStorage.setItem("passkey_last_login_method", method); } catch { /* ignore */ }
    },
    async getUserInfoAsync(token = null) {
      try {
        // Используем переданный токен или берем из store
        const authToken = token || this.$store.getters.getAuthToken;
        
        if (!authToken) {
          throw new Error("No authentication token");
        }
        
        const response = await this.axios.get("/me.json", {
          baseURL: this.baseURL,
          headers: {
            Authorization: authToken,
          },
        });

        if (response.data.active && response.data.role != "agent") {
          this.$store.commit("setCurrentUser", response.data);
          // Set locale
          this.applyLocale(response.data.locale);
          await this.signinSuccessful();
        } else if (response.data.active && response.data.role == "agent" && response.data.agent_setting) {
          if (response.data.agent_setting.access_to_account == false) {
            this.$store.commit("unsetCurrentUser");
            this.isLoading = false;
          } else {
            this.$store.commit("setCurrentUser", response.data);
            // Set locale
            this.applyLocale(response.data.locale);
            await this.signinSuccessful();
          }
        } else {
          this.isLoading = false;
          this.$notify(this.$t("errors.deactivation_title"), this.$t("errors.deactivation_text"), "error", 5000);
        }
      } catch (error) {
        console.log("getUserInfo", error);
        this.$store.commit("unsetCurrentUser");
        this.isLoading = false;
        this.markOAuthSigninFailure();
        this.$notify(this.$t("errors.error"), error.message || error, "error", 5000);
        console.log(error);
      }
    },

    // ===== PASSKEYS (WebAuthn) =====
    async checkPasskeySupport() {
      const isNative = isNativeApp();
      this.passkeySupported = isWebAuthnSupported() || isNative;
      
      if (this.passkeySupported) {
        this.passkeyPlatformAvailable = await isPlatformAuthenticatorAvailable();
        console.log('[Passkeys] Support check:', {
          isNative,
          passkeySupported: this.passkeySupported,
          passkeyPlatformAvailable: this.passkeyPlatformAvailable
        });
      }
    },
    
    async checkUserPasskeys(emailOrPhone) {
      try {
        // POST, а не GET: метод зовётся на КАЖДЫЙ введённый символ, а emailOrPhone —
        // это ПДн (адрес либо телефон). В query-строке он уезжает в access-лог
        // Thruster, в `docker logs` на хосте и в access-логи Selectel LBaaS; тело
        // запроса не попадает ни в один из трёх. Путь и ответ те же — на бэкенде
        // оба глагола ведут в один экшен (rentalman-api#532), поэтому лимит и
        // защита от перечисления общие. Не менять обратно на get.
        const response = await this.axios({
          url: "/api/v1/passkeys/check",
          baseURL: this.baseURL,
          method: "post",
          data: { email: emailOrPhone },
          skipStoredAuth: !!this.oauthApiBase,
        });

        // Сохраняем регион пользователя (для выбора правильного API сервера).
        // FT-018: cable_urls — для CableReconnect mixin (CTR-02).
        if (response.data.region && response.data.api_urls) {
          this.$store.commit("setRegionInfo", {
            region: response.data.region,
            api_urls: response.data.api_urls,
            cable_urls: response.data.cable_urls,
          });
        }

        // Passkey кнопка только если поддерживается
        if (this.passkeySupported) {
          this.userHasPasskeys = response.data.has_passkeys === true;
        }
      } catch (error) {
        // Игнорируем ошибки — не показываем кнопку Passkey
        this.userHasPasskeys = false;
      }
    },
    
    async loginWithPasskey() {
      if (!this.passkeySupported) {
        this.$notify(this.$t("errors.error"), this.$t("passkeys.not_supported"), "error", 5000);
        return;
      }
      
      this.passkeyLoading = true;
      
      // FT-079 fix: остановить in-flight conditional-UI запрос перед модальным
      // navigator.credentials.get() — иначе два параллельных get() конфликтуют
      // и вход по passkey падает (NotAllowedError «A request is already pending»).
      this._passkeyModalActive = true;
      if (this._passkeyCUIAbort) {
        this._passkeyCUIAbort.abort();
        this._passkeyCUIAbort = null;
      }

      try {
        // 1. Получаем опции аутентификации (с email для показа конкретных ключей пользователя)
        const optionsResponse = await this.axios({
          url: "/api/v1/passkeys/authentication_options",
          baseURL: this.baseURL,
          method: "post",
          data: { email: this.emailOrPhone },
          skipStoredAuth: !!this.oauthApiBase,
        });
        
        const authSessionId = optionsResponse.data.auth_session_id;
        
        // 2. Получаем credential (использует нативный плагин или браузерный API)
        let credentialData;
        try {
          credentialData = await getCredential(optionsResponse.data);
        } catch (credError) {
          // Обработка специфических ошибок WebAuthn
          if (credError.name === 'NotAllowedError') {
            this.$notify(this.$t("errors.attention"), this.$t("passkeys.cancelled"), "warning", 5000);
          } else if (credError.name === 'OperationError' || 
                     (credError.message && credError.message.includes('Non-recoverable'))) {
            // Passkey не найден или создан на другом домене
            this.$notify(this.$t("errors.error"), this.$t("passkeys.passkey_not_found"), "error", 10000);
          } else if (credError.name === 'SecurityError') {
            // Проблема с RP ID / доменом
            this.$notify(this.$t("errors.error"), this.$t("passkeys.passkey_invalid_domain"), "error", 10000);
          } else {
            // Другие ошибки - показываем детали
            const errorDetails = `${credError.name || 'Error'}: ${credError.message || credError}`;
            this.$notify(this.$t("errors.error"), errorDetails, "error", 10000);
          }
          return;
        }
        
        if (!credentialData) {
          this.$notify(this.$t("errors.error"), this.$t("passkeys.cancelled"), "warning", 5000);
          return;
        }
        
        // 3. Отправляем credential на сервер вместе с auth_session_id
        const response = await this.axios({
          url: "/api/v1/passkeys/authenticate",
          baseURL: this.baseURL,
          method: "post",
          data: {
            credential: credentialData,
            auth_session_id: authSessionId
          },
          skipStoredAuth: !!this.oauthApiBase,
        });
        
        // 4. Обрабатываем успешный вход
        this.$store.commit("setUserInfo", response);
        this.$store.commit("setUserSettings", response.data.user_settings);

        const token = response.headers.authorization;

        Cookies.set("auth_token", token, {
          expires: 365,
        });
        this.markLoginMethod("passkey"); // FT-079
        markEnrolledHere(response.data.user && response.data.user.id); // FT-079: этот аккаунт использовал passkey на устройстве → nudge не нужен
        
        // Обновляем rememberedLogin если включена опция
        this.updateRememberedLogin(response.data.user_settings, this.emailOrPhone || response.data.user?.email);
        
        // Set theme
        if (response.data.user_settings && response.data.user_settings.dark_theme) {
          this.$vuetify.theme.dark = true;
        } else {
          this.$vuetify.theme.dark = false;
        }
        
        this.$notify(this.$t("passkeys.login_success"), "", "success", 3000);
        
        // Continue with normal sign in flow
        await this.getUserInfoAsync(token);
        
      } catch (error) {
        console.error("Passkey authentication error:", error);
        this.markOAuthSigninFailure();
        
        if (error.name === 'NotAllowedError') {
          this.$notify(this.$t("errors.attention"), this.$t("passkeys.cancelled"), "warning", 5000);
        } else if (error.name === 'AbortError') {
          // User cancelled - do nothing
        } else {
          const errorMessage = error.response?.data?.error || this.$t("passkeys.auth_failed");
          this.$notify(this.$t("errors.error"), errorMessage, "error", 5000);
        }
      } finally {
        this.passkeyLoading = false;
        this._passkeyModalActive = false; // FT-079 fix: снова разрешить conditional UI
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.otp-input-container {
  margin: 20px 0;
}

.otp-fields-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  position: relative;
}

.otp-digit-field {
  width: 50px !important;
  max-width: 50px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ::v-deep .v-input__control {
    min-height: 56px !important;
  }
  
  ::v-deep .v-text-field__details {
    display: none;
  }
  
  ::v-deep input {
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    font-family: 'Roboto Mono', monospace;
    padding: 0 !important;
    caret-color: transparent;
    transition: color 0.3s ease;
  }
  
  ::v-deep .v-input__slot {
    min-height: 56px !important;
    transition: all 0.3s ease;
  }
  
  &.v-input--is-focused {
    ::v-deep fieldset {
      border-color: #1976d2 !important;
      border-width: 2px !important;
    }
  }
  
  // Стили для успешной верификации
  &.otp-verified {
    animation: successPulse 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    
    ::v-deep fieldset {
      border-color: #4caf50 !important;
      border-width: 2px !important;
      box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.15);
    }
    
    ::v-deep .v-input__slot {
      background: linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%);
    }
    
    ::v-deep input {
      color: #2e7d32;
    }
  }
  
  // Стили для ошибки верификации
  &.otp-error {
    animation: errorShake 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97);
    
    ::v-deep fieldset {
      border-color: #f44336 !important;
      border-width: 2px !important;
      box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.15);
    }
    
    ::v-deep .v-input__slot {
      background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
    }
    
    ::v-deep input {
      color: #c62828;
    }
  }
}

@keyframes successPulse {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.05);
  }
  50% {
    transform: scale(0.98);
  }
  75% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-8px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(8px);
  }
}

// Адаптив для мобильных устройств
@media (max-width: 480px) {
  .otp-digit-field {
    width: 40px !important;
    max-width: 40px;
    
    ::v-deep .v-input__control {
      min-height: 48px !important;
    }
    
    ::v-deep .v-input__slot {
      min-height: 48px !important;
    }
    
    ::v-deep input {
      font-size: 20px;
    }
  }
  
  .otp-fields-wrapper {
    gap: 8px;
  }
}

.v-alert {
  border-radius: 8px;
}

.text--secondary {
  font-size: 13px;
}

.links-section {
  a {
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .password-link {
    font-size: 13px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
      text-decoration: underline;
    }
  }
}

// Smooth expand transition
.v-card-text {
  padding-top: 20px;
}

// Passkey button
.passkey-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%) !important;
  color: white !important;
  border: none !important;
  
  &:hover {
    background: linear-gradient(135deg, #7986cb 0%, #5c6bc0 100%) !important;
    box-shadow: 0 4px 12px rgba(92, 107, 192, 0.4);
  }
  
  .v-icon {
    color: white !important;
  }
}
</style>
